import { PolymerElement, html } from 'polymer';

import {
  AsHIC,
  BehaviorA11yGestures,
  BehaviorA11yKeyboard,
  BehaviorElement,
  HIP
} from '@eab-eip/hi-component/hi-component';
import { ItemElement } from '@eab-eip/hi-list/hi-item';
import { ListElement } from '@eab-eip/hi-list/hi-list';
const moment = globalThis['moment'];

/**
 * @customElement
 * @polymer
 * @appliesMixin BehaviorElement
 * @appliesMixin BehaviorTime
 */
export class TimeListElement extends BehaviorA11yGestures(
  BehaviorA11yKeyboard(BehaviorElement(AsHIC(PolymerElement)))
) {
  static get is() {
    return 'hi-time-list';
  }

  static get template() {
    return html`
      <style is="custom-style" include="hi-theme"></style>
      <hi-list
        id="timesList"
        on-trigger-ended="__timeList__onTriggerEnded"
        on-set-focus-item-focused="__timeList__onSetFocusItemFocused"
      >
        <dom-repeat items="[[times]]" as="time">
          <template>
            <hi-item id="[[_getTimeID(time)]]" on-touchstart="__timeList__onSelect" on-mousedown="__timeList__onSelect">
              [[ time ]]
            </hi-item>
          </template>
        </dom-repeat>
      </hi-list>
    `;
  }

  $: {
    timesList: ListElement;
  };

  selected = 0;
  readonly times: string[];

  timeParseFormats: string[];
  timeFormat: string;

  displayFormat = 'h:mm A';
  max = '11:59pm';
  min = '12:00am';
  step = 15;

  static get properties() {
    return {
      selected: {
        type: Number,
        value: 0
      },
      times: {
        type: Array,
        computed: '__timesCompute(min, max, step, displayFormat)',
        value: () => []
      },
      max: {
        type: String
      },
      min: {
        type: String
      },
      step: {
        type: Number
      },
      displayFormat: {
        type: String
      },
      timeParseFormats: {
        type: Array,
        readOnly: true,
        value: () => ['h:mm', 'hmm', 'h', 'h:mma', 'hmma', 'ha', 'HHmm']
      },
      timeFormat: {
        type: String,
        readOnly: true,
        value: () => TimeListElement.timeFormat
      }
    };
  }

  static get api() {
    return [
      'chooseFocusedTime',
      'shiftFocusDown',
      'shiftFocusUp',
      'findTime',
      'roundTime',
      'parseTime',
      'parseUserTime'
    ];
  }

  static get timeFormat() {
    return 'h:mm A';
  }

  connectedCallback() {
    super.connectedCallback();
    this.setAttribute('role', 'group');
  }

  roundTime(_time: string, step = 5) {
    const time = moment(_time);

    return time
      .clone()
      .startOf('hour')
      .add(Math.round(time.minute() / step) * step, 'minutes');
  }

  parseUserTime(time: string) {
    return moment(time, this.timeParseFormats, true);
  }

  /**
   * @note currently duplicated in hi-behavior-input-time to share with datepicker
   * @todo we should move to [hi-component]
   *
   */
  parseTime(time: string, format = this.timeFormat || 'h:mm A') {
    if (typeof time === 'object') {
      return time;
    }

    return time === 'now' ? moment() : moment(time, format);
  }

  shiftFocusDown() {
    this.$.timesList.shiftFocusDown();
  }

  shiftFocusUp() {
    this.$.timesList.shiftFocusUp();
  }

  chooseFocusedTime() {
    this.$.timesList.chooseFocused();
    this._fireTimeSelect(this.$.timesList.focusedValueFormatted.trim());
  }

  focusedTime() {
    // TODO: we passed nothing initially, which is an error - this is to get TS running
    // this method wants an item
    return this.$.timesList.focusItem(undefined as never);
  }

  /**
   * find the time string in the list of times
   * @param {*} _time time string (eg. 12:00 am)
   */
  findTime(_time: string) {
    const time = this.parseTime(_time);

    if (time.isValid()) {
      const timeList = this.$.timesList.slotChildren;
      const roundedTime = this.roundTime(time, this.step);
      const matchTime = roundedTime.format(this.displayFormat);

      return timeList.find((item) => {
        return (item.textContent || '').indexOf(matchTime) > 0;
      });
    }

    return undefined;
  }

  protected _getTimeID(time: string) {
    return `time-option-${this.parseTime(time).format('hhmma')}`;
  }

  __timesCompute(min: string, max: string, step: number, displayFormat: string) {
    const minTime = this.parseTime(min) || this.parseTime('12:00am');
    const maxTime = this.parseTime(max) || this.parseTime('11:59pm');
    const currentTime = minTime.clone();
    const times = [];

    const safeLoop = 250;
    let i = 0;

    while (currentTime.isSameOrBefore(maxTime) && i < safeLoop) {
      times.push(currentTime.format(displayFormat));
      currentTime.add(step, 'minutes');
      i += 1;
    }

    return times;
  }

  __timeList__onSelect(event: CustomEvent) {
    if (event.target) {
      this._fireTimeSelect(event.target['innerText']);
    }
  }

  __timeList__onTriggerEnded(event: CustomEvent) {
    if (event.detail.item) {
      this._fireTimeSelect(event.detail.item.textContent.trim());
    }
  }

  __timeList__onSetFocusItemFocused(event: CustomEvent) {
    if (event.detail.item) {
      this._fireFocusedItem(event.detail.item);
    }
  }

  _fireFocusedItem(item: ItemElement) {
    this._dispatchEvent('item-focus', {
      item: item
    });
  }

  _fireTimeSelect(time: string) {
    this._dispatchEvent('time-select', {
      time: time
    });
  }
}

HIP.factory.element(TimeListElement);
